import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { usePanelContext } from '~/eds/src/components/Panel/PanelContext';
import type { MenuActions } from '~/eds/src/types/MenuActions';
import { TicketPermissionType } from '~/enums/TicketPermissionType';
import { DOCUMENT_NAME_MAX_LENGTH } from '~/features/document-handler/constants';
import {
  AddToGroupPanel,
  AddToGroupPanelId,
} from '~/features/groups/AddToGroupButton/AddToGroupPanel';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useCanEditEntityGroups } from '~/hooks/useCanEditEntityGroups';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { openInNewTab } from '~/utils/browser';

import { MODAL_DELETE, MODAL_NAME } from '../../../types/modal.types';
import DeleteDocumentWithReasonModal from '../../Modals/DeleteDocumentWithReasonModal/DeleteDocumentWithReasonModal';
import EcModal from '../../Shared/EcModal/EcModal';

const TWO_MINUTES = 2 * 60 * 1000; // 2 minutes in milliseconds

interface DeleteDocumentModalProps {
  hasDeleteWithReasonEnabled: boolean;
  isVisible: boolean;
  documentName: string;
  onHide: () => void;
  onConfirmDelete: () => void;
}

const DeleteDocumentModal = ({
  hasDeleteWithReasonEnabled,
  isVisible,
  documentName,
  onHide,
  onConfirmDelete,
}: DeleteDocumentModalProps) => {
  const intl = useIntl();

  if (hasDeleteWithReasonEnabled) {
    return (
      <DeleteDocumentWithReasonModal
        type="document"
        isVisible={isVisible}
        count={1}
        selectedItemName={`"${documentName}"`}
        hideModal={onHide}
        onConfirmDelete={onConfirmDelete}
      />
    );
  }

  return (
    <Modal
      isVisible
      primaryAction={{
        text: intl.formatMessage({
          id: 'EditDocumentMenu.deleteButton',
          defaultMessage: 'Delete',
          description: 'Delete button text',
        }),
        variant: 'danger',
        onClick: () => {
          onConfirmDelete();
          onHide();
        },
      }}
      onHide={onHide}
      onCancel={onHide}
      title={intl.formatMessage({
        id: 'EditDocumentMenu.deleteTitle',
        defaultMessage: 'Delete document?',
        description: 'Title for delete document modal',
      })}
    >
      <Markdown
        text={intl.formatMessage(
          {
            id: 'EditDocumentMenu.deleteConfirmation',
            defaultMessage:
              "Are you sure you want to delete {documentName}? You won't be able to undo this action.",
            description:
              'Confirmation message for deleting document. Example: documentName = "Document Name"',
          },
          { documentName: documentName ?? '' },
        )}
      />
    </Modal>
  );
};

interface RenameDocumentModalProps {
  parentFolderId: string;
  currentName: string;
  onNameChange: (newName: string) => void;
  onHide: () => void;
  nameMaxLength: number;
}

const RenameDocumentModal = ({
  parentFolderId,
  currentName,
  onNameChange,
  onHide,
  nameMaxLength,
}: RenameDocumentModalProps) => {
  const intl = useIntl();

  return (
    <EcModal
      modalType={MODAL_NAME}
      width="560px"
      title={intl.formatMessage({
        id: 'EditDocumentMenu.renameTitle',
        defaultMessage: 'Rename Document',
        description: 'Title for rename document modal',
      })}
      labelText={intl.formatMessage({
        id: 'EditDocumentMenu.documentNameLabel',
        defaultMessage: 'DOCUMENT NAME',
        description: 'Label for document name field',
      })}
      itemsParentFolderId={parentFolderId}
      itemsCurrentName={currentName}
      confirmButtonText={intl.formatMessage({
        id: 'EditDocumentMenu.saveButton',
        defaultMessage: 'Save',
        description: 'Save button text',
      })}
      handleNameChange={onNameChange}
      hideModal={onHide}
      nameMaxLength={nameMaxLength}
    />
  );
};

interface DocumentData {
  id?: string;
  document_name?: string;
  parent_folder?: string;
  date_added?: string;
  document?: number;
}

interface IntegrationUrls {
  procurementContractUrl?: string;
}

interface Props {
  documentData?: DocumentData;
  integrationUrls?: IntegrationUrls;
  hasDeleteWithReasonEnabled?: boolean;
  handleDocumentRename: (newName: string) => void;
  handleDocumentDelete: () => void;
}

type ModalType = typeof MODAL_DELETE | typeof MODAL_NAME | null;

export const EditDocumentMenu = ({
  documentData,
  integrationUrls,
  hasDeleteWithReasonEnabled,
  handleDocumentRename,
  handleDocumentDelete,
}: Props) => {
  const intl = useIntl();
  const client = useClientSubDomain();
  const { navigate: routingNavigate } = useRouting();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);

  const { openPanel } = usePanelContext();
  const [currentModal, setCurrentModal] = useState<ModalType>(null);
  const [isProcurementUrlDisabled, setIsProcurementUrlDisabled] =
    useState<boolean>(false);

  const { hasPermission: hasEditEntityGroupsPermission } =
    useCanEditEntityGroups();

  const hasGroupedTicketAndDocs = useFlag(FlagType.GroupedTicketsAndDocs);
  const canAddToGroup =
    hasEditEntityGroupsPermission && hasGroupedTicketAndDocs;
  const hasAmendmentsFlag = useFlag(FlagType.WSSIntegrationM2Amendments);

  const { data: documentInfo } = api.endpoints.getDocumentInformation.useQuery(
    { documentId: documentData?.id ?? '', formatDate, formatNumber },
    { skip: !documentData?.id },
  );

  const { data: ticketSummary, isFetching: isFetchingTicketSummary } =
    api.endpoints.getTicketSummary.useQuery(
      { ticketId: documentInfo?.ticketId ?? '' },
      { skip: !documentInfo?.ticketId },
    );

  const hasTicketViewPermission = useMemo(
    () =>
      ticketSummary?.permissions?.includes(TicketPermissionType.TicketView) ??
      false,
    [ticketSummary?.permissions],
  );

  const shouldShowViewTicket = useMemo(
    () => Boolean(documentInfo?.ticketId),
    [documentInfo?.ticketId],
  );

  useEffect(() => {
    if (documentData?.date_added) {
      const dateAdded = new Date(documentData.date_added).getTime();
      const dateCreated = Date.now() - dateAdded;

      if (dateAdded < Date.now() && dateCreated < TWO_MINUTES) {
        setIsProcurementUrlDisabled(true);
        timeoutIdRef.current = setTimeout(() => {
          setIsProcurementUrlDisabled(false);
        }, TWO_MINUTES - dateCreated);
      }
    }

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [documentData?.date_added]);

  const handleShowModal = useCallback((modal: ModalType) => {
    setCurrentModal(modal);
  }, []);

  const handleHideModal = useCallback(() => {
    setCurrentModal(null);
  }, []);

  const menuItems = useMemo(() => {
    const shouldDisableAmend = ticketSummary?.isAmendable !== true;

    const items = [
      ...(shouldShowViewTicket
        ? [
            {
              label: intl.formatMessage({
                id: 'EditDocumentMenu.viewTicketAction',
                defaultMessage: 'View Ticket',
                description: 'View Ticket menu item text',
              }),
              value: 'view-ticket',
              disabled: !hasTicketViewPermission,
              tooltip: !hasTicketViewPermission
                ? intl.formatMessage({
                    id: 'EditDocumentMenu.noTicketPermission',
                    defaultMessage:
                      'You do not have permission to view this ticket',
                    description:
                      'Tooltip when user lacks permission to view ticket',
                  })
                : '',
              onClick: () => {
                const ticketUrl = `/${client}/workflow/tickets/${documentInfo?.ticketId}`;
                openInNewTab(ticketUrl);
              },
            },
          ]
        : []),
      ...(hasAmendmentsFlag && documentInfo?.isWssContract
        ? [
            {
              disabled: shouldDisableAmend,
              rightIcon: isFetchingTicketSummary
                ? ('loading' as const)
                : undefined,
              disabledReason: (() => {
                if (isFetchingTicketSummary) {
                  return intl.formatMessage({
                    id: 'EditDocumentMenu.loadingTicketSummary',
                    defaultMessage: 'Loading ticket summary',
                    description:
                      'used as a tooltip, shown when customers hover a menu option and we are still fetching data',
                  });
                }

                if (shouldDisableAmend) {
                  return intl.formatMessage({
                    id: 'EditDocumentMenu.notAmendable',
                    defaultMessage: 'This is not the latest document',
                    description:
                      'used as a tooltip, shown when customers hover a menu option and the document is not the latest version. It explains why the option theyre trying to use is disabled.',
                  });
                }
              })(),
              label: intl.formatMessage({
                id: 'EditDocumentMenu.amendAction',
                defaultMessage: 'Amend',
                description: 'Amend menu item text',
              }),
              value: 'amend',
              onClick: () => {
                if (shouldDisableAmend) return;

                const amendPath = generatePath(
                  RoutePathType.IntegrateWorkflowAmendIntakeForm,
                  {
                    contractRecordUniqueId:
                      ticketSummary.contractRecordUniqueId!,
                  },
                );
                routingNavigate(amendPath);
              },
            },
          ]
        : []),
      {
        label: intl.formatMessage({
          id: 'EditDocumentMenu.renameAction',
          defaultMessage: 'Rename',
          description: 'Rename menu item text',
        }),
        value: 'rename-document',
        onClick: () => handleShowModal(MODAL_NAME),
      },
      {
        label: intl.formatMessage({
          id: 'EditDocumentMenu.deleteAction',
          defaultMessage: 'Delete',
          description: 'Delete menu item text',
        }),
        value: 'delete-document',
        onClick: () => handleShowModal(MODAL_DELETE),
      },
      ...(canAddToGroup
        ? [
            {
              label: intl.formatMessage({
                id: 'EditDocumentMenu.addToGroupAction',
                defaultMessage: 'Add to Group',
                description: 'Add to group menu item text',
              }),
              value: 'add-to-group',
              onClick: () => openPanel(AddToGroupPanelId),
            },
          ]
        : []),
    ];

    const menuActions: MenuActions<string> = [
      ...(integrationUrls?.procurementContractUrl
        ? [
            {
              options: [
                {
                  label: intl.formatMessage({
                    id: 'EditDocumentMenu.goToProcurement',
                    defaultMessage: 'Go to Procurement',
                    description: 'Link to procurement',
                  }),
                  value: 'go-procurement-contract',
                  disabled: isProcurementUrlDisabled,
                  href: integrationUrls.procurementContractUrl,
                  tooltip: isProcurementUrlDisabled
                    ? intl.formatMessage({
                        id: 'EditDocumentMenu.sendingDocumentToProcurement',
                        defaultMessage:
                          'Sending document to Procurement, this may take a few minutes',
                        description: 'Tooltip for disabled procurement link',
                      })
                    : '',
                },
              ],
              label: '',
            },
          ]
        : []),
      { options: items, label: '' },
    ];
    return menuActions;
  }, [
    ticketSummary?.isAmendable,
    ticketSummary?.contractRecordUniqueId,
    shouldShowViewTicket,
    intl,
    hasTicketViewPermission,
    hasAmendmentsFlag,
    documentInfo?.isWssContract,
    documentInfo?.ticketId,
    isFetchingTicketSummary,
    canAddToGroup,
    integrationUrls?.procurementContractUrl,
    isProcurementUrlDisabled,
    client,
    routingNavigate,
    handleShowModal,
    openPanel,
  ]);

  return (
    <>
      <ActionsMenu
        name={intl.formatMessage({
          id: 'EditDocumentMenu.menuTitle',
          defaultMessage: 'Edit Document Menu',
          description: 'Title for edit document menu',
        })}
        actions={menuItems}
      />

      {currentModal === MODAL_DELETE ? (
        <DeleteDocumentModal
          hasDeleteWithReasonEnabled={!!hasDeleteWithReasonEnabled}
          isVisible
          documentName={documentData?.document_name ?? ''}
          onHide={handleHideModal}
          onConfirmDelete={handleDocumentDelete}
        />
      ) : null}

      {currentModal === MODAL_NAME ? (
        <RenameDocumentModal
          parentFolderId={documentData?.parent_folder ?? ''}
          currentName={documentData?.document_name ?? ''}
          onNameChange={handleDocumentRename}
          onHide={handleHideModal}
          nameMaxLength={DOCUMENT_NAME_MAX_LENGTH}
        />
      ) : null}

      {canAddToGroup && (
        <AddToGroupPanel
          preselectedItems={[
            {
              resourceId: String(documentData?.document || ''),
              resourceType: 'document',
            },
          ]}
        />
      )}
    </>
  );
};

export default EditDocumentMenu;
